import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, ComponentLoader, Heading, InPageAlert } from '@snsw-gel/react';
import { cancelEventBooking, getEventDetails } from 'app/services/api/internal/API';
import { openModal } from 'app/services/events/EventService';
import { useEventFlow } from 'app/contexts/EventFlowContext';
import EventLayout from './EventLayout';
import {
  StyledSection,
  BookingCard,
  BookingDetailsRow,
  BookingLabel,
  BookingValue,
  BookingDetailsHeader,
  ButtonRow,
} from './EventManagement.styled';

interface EventManagementProps {
  eventId: string;
  bookingId: string;
}

interface EventInfo {
  title?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  location?: string;
}

const formatEventDateTime = (startTime?: string, endTime?: string): string => {
  if (!startTime) return '';
  const start = new Date(startTime);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  let formatted = start.toLocaleDateString('en-AU', options);
  if (endTime) {
    const end = new Date(endTime);
    const endFormatted = end.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    formatted += `-${endFormatted}`;
  }
  return formatted;
};

const EventManagement: React.FC<EventManagementProps> = ({ eventId, bookingId }) => {
  const history = useHistory();
  const { eventDetails, setEventDetails } = useEventFlow();
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEventInfo = async () => {
      if (eventDetails) {
        setEventInfo(eventDetails);
        setLoading(false);
        return;
      }
      try {
        const response = await getEventDetails(eventId);
        const data = (response as any)?.data || response;
        setEventInfo(data);
        setEventDetails(data);
      } catch {
        // silent fail
      } finally {
        setLoading(false);
      }
    };
    loadEventInfo();
  }, [eventId]);

  const handleCancelBooking = () => {
    openModal('INFO', {
      title: 'Cancel your booking',
      message: 'Are you sure you want to cancel your event registration?',
      buttons: {
        confirm: { text: 'Yes, cancel booking', handler: confirmCancel },
        close: { text: 'No, close window' },
      },
    });
  };

  const confirmCancel = async () => {
    if (!bookingId) {
      setError('Booking ID not found.');
      return;
    }
    setCancelling(true);
    setError(null);
    try {
      await cancelEventBooking(eventId, bookingId, {});
      setCancelled(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Unable to cancel booking. Please try again.';
      setError(message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading || cancelling) {
    return <ComponentLoader label="Loading" fullPage />;
  }

  if (cancelled) {
    return (
      <EventLayout showStepper={false} title="Event booking management">
        <InPageAlert variant="success" title="Your booking has been cancelled">
          <p>Your event registration has been successfully cancelled.</p>
        </InPageAlert>
        <StyledSection>
          <Button variant="link" onClick={() => history.push('/')}>
            Return home
          </Button>
        </StyledSection>
      </EventLayout>
    );
  }

  return (
    <EventLayout showStepper={false} title="Event booking management">
      {error && (
        <InPageAlert variant="error" title="Error">
          <p>{error}</p>
        </InPageAlert>
      )}

      <BookingCard>
        <Heading level={2}>Booking details</Heading>

        <BookingDetailsHeader>
          <div>
            <BookingDetailsRow>
              <BookingLabel>Date and time</BookingLabel>
              <BookingValue>
                {formatEventDateTime(eventInfo?.eventStartTime, eventInfo?.eventEndTime) || '—'}
              </BookingValue>
            </BookingDetailsRow>
            <BookingDetailsRow>
              <BookingLabel>Event name</BookingLabel>
              <BookingValue>{eventInfo?.title || '—'}</BookingValue>
            </BookingDetailsRow>
            <BookingDetailsRow>
              <BookingLabel>Location</BookingLabel>
              <BookingValue>{eventInfo?.location || 'Online'}</BookingValue>
            </BookingDetailsRow>
          </div>
        </BookingDetailsHeader>

        <ButtonRow>
          <Button variant="secondary" onClick={handleCancelBooking}>
            Cancel booking
          </Button>
        </ButtonRow>
      </BookingCard>
    </EventLayout>
  );
};

export default EventManagement;
